/**
 * 📋 Delegate-task modal — the shared create/delegate surface used by the floating DelegateTaskButton and
 * the contextual CreateTaskHere buttons. Captures the screen-context (page_url + view_tab + an optional
 * annotated screenshot) so the assignee can deep-link back to "the screen this came from". Annotation +
 * retry-without-image logic mirror ReportButton; the payload targets createTask instead of /tool-feedback.
 */
import { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { shrinkDataUrl, captureScreen } from '../lib/screenshot';
import { createTask, TASK_PRIORITY_LABEL, type TaskContext } from '../lib/api';
import AssigneePicker, { type Assignee } from './AssigneePicker';
import { Modal } from './ui/Modal';

type Tool = 'box' | 'pen';
interface Shape { tool: Tool; pts: { x: number; y: number }[]; }

export interface DelegateTaskModalProps {
  open: boolean;
  onClose: () => void;
  context: TaskContext;
  /** Screenshot captured BEFORE the modal opened (so the modal isn't in the shot). Null = none. */
  initialScreenshot?: string | null;
  pageUrl: string;
  viewTab: string | null;
  packageId?: string;
  prefillTitle?: string;
}

export default function DelegateTaskModal({
  open, onClose, context, initialScreenshot, pageUrl, viewTab, packageId, prefillTitle,
}: DelegateTaskModalProps) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(prefillTitle ?? '');
  const [assignee, setAssignee] = useState<Assignee | null>(null);
  const [priority, setPriority] = useState('medium');
  const [due, setDue] = useState('');
  const [desc, setDesc] = useState('');
  const [tool, setTool] = useState<Tool>('box');
  const [img, setImg] = useState<string | null>(initialScreenshot ?? null);
  const [done, setDone] = useState(false);
  const [imageDropped, setImageDropped] = useState(false);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const bgRef = useRef<HTMLImageElement | null>(null);
  const shapesRef = useRef<Shape[]>([]);
  const drawingRef = useRef(false);

  useEffect(() => { if (open) { setTitle(prefillTitle ?? ''); setImg(initialScreenshot ?? null); } }, [open, prefillTitle, initialScreenshot]);

  const redraw = useCallback(() => {
    const c = canvasRef.current, bg = bgRef.current; if (!c || !bg) return;
    const ctx = c.getContext('2d'); if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(bg, 0, 0, c.width, c.height);
    ctx.strokeStyle = '#ff2d2d'; ctx.lineWidth = 3; ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    for (const s of shapesRef.current) {
      const a = s.pts[0], b = s.pts[s.pts.length - 1];
      if (!a || !b) continue;
      if (s.tool === 'box' && s.pts.length >= 2) ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
      else if (s.tool === 'pen') { ctx.beginPath(); ctx.moveTo(a.x, a.y); for (const p of s.pts.slice(1)) ctx.lineTo(p.x, p.y); ctx.stroke(); }
    }
  }, []);

  const loadBg = useCallback((dataUrl: string) => {
    const im = new Image();
    im.onload = () => {
      const c = canvasRef.current; if (!c) return;
      c.width = im.width; c.height = im.height;
      bgRef.current = im; shapesRef.current = []; redraw();
    };
    im.src = dataUrl;
  }, [redraw]);

  useEffect(() => { if (open && img) loadBg(img); }, [open, img, loadBg]);

  const at = (e: React.MouseEvent) => {
    const c = canvasRef.current!, r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (c.width / r.width), y: (e.clientY - r.top) * (c.height / r.height) };
  };
  const onDown = (e: React.MouseEvent) => { if (!bgRef.current) return; drawingRef.current = true; shapesRef.current.push({ tool, pts: [at(e)] }); };
  const onMove = (e: React.MouseEvent) => {
    if (!drawingRef.current) return;
    const s = shapesRef.current[shapesRef.current.length - 1]; if (!s) return;
    if (tool === 'box') { const start = s.pts[0]; s.pts = start ? [start, at(e)] : [at(e)]; }
    else s.pts.push(at(e));
    redraw();
  };
  const onUp = () => { drawingRef.current = false; };
  const onCaptureScreen = async () => { const url = await captureScreen(); if (url) { setImg(url); loadBg(url); } };

  const submit = useMutation({
    mutationFn: async () => {
      setImageDropped(false);
      const raw = (canvasRef.current && bgRef.current) ? canvasRef.current.toDataURL('image/jpeg', 0.85) : (img ?? undefined);
      const shot = raw ? await shrinkDataUrl(raw) : undefined;
      const ctx: TaskContext = { ...context, view_tab: viewTab ?? context.view_tab };
      const send = (screenshot?: string) => createTask({
        title: title.trim(), assignee_user_id: assignee?.uuid, assignee_name: assignee?.name, priority,
        due_date: due || undefined, description: desc || undefined,
        package_id: packageId || context.package_id || undefined,
        page_url: pageUrl, view_tab: viewTab ?? undefined,
        context_json: JSON.stringify(ctx), screenshot,
      });
      try { return await send(shot); }
      catch (e) { if (shot) { setImageDropped(true); return await send(undefined); } throw e; }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tasks'] }); setDone(true); },
  });

  const close = () => { setDone(false); setImageDropped(false); bgRef.current = null; shapesRef.current = []; setAssignee(null); setDue(''); setDesc(''); submit.reset(); onClose(); };
  const toolBtn = (t: Tool, label: string) => (
    <button type="button" onClick={() => setTool(t)} style={{ fontSize: 12, fontWeight: tool === t ? 700 : 400, borderColor: tool === t ? 'var(--accent)' : undefined }}>{label}</button>
  );

  if (!open) return null;
  return (
    <Modal onClose={close} align="center" width={820}>
        {done ? (
          <div style={{ textAlign: 'center', padding: 12 }}>
            <div style={{ fontSize: 28 }}>✅</div>
            <p style={{ margin: '8px 0' }}>
              {imageDropped
                ? '已派发 ✅ — 截图过大未附上，任务已送达。 / Delegated; screenshot was too large so it was sent without it.'
                : '已派发 ✅ — 任务与来源截图已送达受理人。 / Delegated, with the source screenshot.'}
            </p>
            <button type="button" className="btn-primary" onClick={close}>关闭 / Close</button>
          </div>
        ) : (
          <>
            <h3 style={{ marginTop: 0 }}>📋 派发任务 / Delegate a task</h3>
            <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
              受理人可点「打开来源」跳回此屏。在截图上圈出要点（可选）。 · The assignee can jump back to this screen; circle the key spot (optional).
            </p>
            <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end', marginBottom: 8 }}>
              <div className="field" style={{ flex: 2, minWidth: 200, marginBottom: 0 }}>
                <label>标题 / Title</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="例如：复核此屏 / e.g. review this screen" />
              </div>
              <div className="field" style={{ marginBottom: 0, minWidth: 220 }}>
                <label>指派给 / Assign to</label>
                <AssigneePicker value={assignee} onChange={setAssignee} />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>优先级 / Priority</label>
                <select value={priority} onChange={(e) => setPriority(e.target.value)}>
                  {Object.entries(TASK_PRIORITY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label>截止 / Due</label>
                <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
              </div>
            </div>

            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
              来源 / Source: <code>{pageUrl}</code> · {context.label}
            </div>

            {img ? (
              <>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
                  <span className="muted" style={{ fontSize: 12 }}>标注 / Draw:</span>
                  {toolBtn('box', '▭ Box · 框')}
                  {toolBtn('pen', '✏️ Pen · 笔')}
                  <button type="button" style={{ fontSize: 12 }} onClick={() => { shapesRef.current.pop(); redraw(); }}>撤销 / Undo</button>
                  <button type="button" style={{ fontSize: 12 }} onClick={() => { shapesRef.current = []; redraw(); }}>清除 / Clear</button>
                  <button type="button" style={{ fontSize: 12, marginLeft: 'auto' }} onClick={onCaptureScreen} title="Capture the full screen instead">🖥 全屏 / Full screen</button>
                </div>
                <canvas ref={canvasRef} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
                  style={{ width: '100%', border: '1px solid var(--rule)', borderRadius: 6, cursor: 'crosshair', display: 'block' }} />
              </>
            ) : (
              <div className="muted" style={{ padding: 12, border: '1px dashed var(--rule)', borderRadius: 6, fontSize: 13 }}>
                未附截图 / No screenshot — 任务仍会带上来源链接。
                <button type="button" style={{ fontSize: 12, marginLeft: 6 }} onClick={onCaptureScreen}>🖥 截屏 / capture</button>
              </div>
            )}

            <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2}
              placeholder="说明（可选）/ Notes (optional)"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 14, padding: 8, marginTop: 8 }} />
            {submit.isError && <div role="alert" style={{ color: 'var(--bad)', fontSize: 13 }}>{errText(submit.error)}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
              <button type="button" onClick={close}>取消 / Cancel</button>
              <button type="button" className="btn-primary" disabled={!title.trim() || !assignee || submit.isPending} onClick={() => submit.mutate()}>
                {submit.isPending ? '派发中… / sending…' : '派发 / Delegate'}
              </button>
            </div>
          </>
        )}
    </Modal>
  );
}

function errText(e: unknown): string {
  const b = (e as any)?.body;
  return b?.detail ?? b?.error ?? (e as any)?.message ?? '派发失败，请重试 / Failed, try again.';
}
